import { getCoreUtils, nz } from "../../../Admin/kReusableCoreTS/kCoreHandler";
import { kObjectArrayCore } from "../../../Admin/kReusableCoreTS/kCoreObjects";
import { AppTask } from "./AppTask";

// AppTaskList - one contact's tasks (owner, 2026-11-02). Its open count is the strip's rule (`AppTaskFrame.Recount`:
// the items whose state is "open"). Whoever shows a count - the page's "N open" chip, the phone header's Tasks badge -
// LISTENS to the list; nobody watches the page (the old `AppPhoneChatRun` MutationObserver is gone).
// ⭐ ONE notification per change: `SetState` marks the list changed; `Settle` (called once the change is drawn)
// tells every listener once.

export interface IAppTaskListListener {
    Changed(List: AppTaskList): void;
}

export class AppTaskList extends kObjectArrayCore {
    private Listeners: IAppTaskListListener[] = [];
    private Dirty: boolean = false;

    public constructor() {
        super();
        this.NewItem = AppTask;
    }

    public Tasks = (): AppTask[] => this.Items as AppTask[];

    public Add = (Task: AppTask): void => {
        if (getCoreUtils().KErrorMode === true) { return; }
        try {
            this.push(Task);
            this.Dirty = true;
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "AppTaskList.Add");
        }
    };

    public Clear = (): void => {
        if (getCoreUtils().KErrorMode === true) { return; }
        try {
            this.Items = [];
            this.Dirty = true;
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "AppTaskList.Clear");
        }
    };

    public ById = (Id: string): AppTask | null => {
        if (getCoreUtils().KErrorMode === true) { return null; }
        try {
            for (const Task of this.Tasks()) {
                if (Task.Id === nz(Id)) { return Task; }
            }
            return null;
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "AppTaskList.ById");
            return null;
        }
    };

    /** The tasks still open (a reply owed counts; snoozed, done and dismissed do not). */
    public OpenCount = (): number => {
        if (getCoreUtils().KErrorMode === true) { return 0; }
        try {
            let Open: number = 0;
            for (const Task of this.Tasks()) {
                if (Task.IsOpen() === true) { Open = Open + 1; }
            }
            return Open;
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "AppTaskList.OpenCount");
            return 0;
        }
    };

    /** Every contact's "N to do" on `Today` (`AppTask.CountsAsToDo`), keyed by the contact's href. */
    public ToDoByHref = (Today: string): Record<string, number> => {
        const Out: Record<string, number> = {};
        if (getCoreUtils().KErrorMode === true) { return Out; }
        try {
            for (const Task of this.Tasks()) {
                if (Task.CountsAsToDo(Today) === false) { continue; }
                Out[Task.Href] = (Out[Task.Href] || 0) + 1;
            }
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "AppTaskList.ToDoByHref");
        }
        return Out;
    };

    /** `Id` takes `State`; the listeners hear it at the next `Settle`. */
    public SetState = (Id: string, State: string, Until: string = ""): boolean => {
        if (getCoreUtils().KErrorMode === true) { return false; }
        try {
            const Task: AppTask | null = this.ById(Id);
            if (Task === null) { return false; }
            const Changed: boolean = Task.SetState(State, Until);
            if (Changed === true) { this.Dirty = true; }
            return Changed;
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "AppTaskList.SetState");
            return false;
        }
    };

    public OnChanged = (Listener: IAppTaskListListener): void => {
        if (getCoreUtils().KErrorMode === true) { return; }
        try {
            if (this.Listeners.indexOf(Listener) < 0) { this.Listeners.push(Listener); }
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "AppTaskList.OnChanged");
        }
    };

    /** Something changed since the last call: every listener is told, once. */
    public Settle = (): void => {
        if (getCoreUtils().KErrorMode === true) { return; }
        try {
            if (this.Dirty === false) { return; }
            this.Dirty = false;
            for (const Listener of this.Listeners) { Listener.Changed(this); }
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "AppTaskList.Settle");
        }
    };
}
